import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import {
  FiTrash2,
  FiUploadCloud,
  FiDownload,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";

const DepartmentManagement = () => {
  const { token } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [feedback, setFeedback] = useState({ text: "", type: "" });

  // Mode switcher for adding department
  const [activeMode, setActiveMode] = useState("single"); // "single" | "import"
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    department: "",
    email: "",
    password: "",
  });

  // Fetch departments from backend
  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/auth/departments`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.data && res.data.data) {
        setDepartments(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch departments:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Form Change
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: null });
    }
  };

  // Handle Create Department Submit
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.department || !formData.department.trim()) {
      errors.department = "Department name is required";
    }
    if (!formData.email || !formData.email.trim()) {
      errors.email = "Official email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = "Please enter a valid email address";
    }
    if (!formData.password || !formData.password.trim()) {
      errors.password = "Access password is required";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setActionLoading(true);
      setFeedback({ text: "", type: "" });
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/auth/create-department`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setFeedback({
        text: res.data?.message || `Department "${formData.department}" created!`,
        type: "success",
      });
      setFormData({
        department: "",
        email: "",
        password: "",
      });
      setFormErrors({});
      fetchDepartments();
    } catch (err) {
      setFeedback({
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to create department account",
        type: "danger",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Delete Department
  const handleDeleteDepartment = async (dept) => {
    if (
      !window.confirm(
        `Are you sure you want to remove the "${dept.department}" department account (${dept.email})?`
      )
    ) {
      return;
    }

    try {
      setActionLoading(true);
      const res = await axios.delete(
        `${process.env.REACT_APP_API_BASE}/auth/delete-department/${dept._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setFeedback({
        text: res.data?.message || `Department "${dept.department}" removed!`,
        type: "success",
      });
      fetchDepartments();
    } catch (err) {
      setFeedback({
        text: err?.response?.data?.message || "Failed to remove department",
        type: "danger",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Download Sample CSV Template for Departments (RFC-4180 with UTF-8 BOM)
  const handleDownloadTemplate = () => {
    const headers = ["Department Name", "Official Email", "Password"];
    const escapeVal = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const sampleRows = [
      ["Examination", "exam@mujconvocation.in", "exam123"],
      ["Security & Transport", "security@mujconvocation.in", "sec123"],
      ["Student Welfare", "dsw@mujconvocation.in", "dsw123"],
    ];
    const csvContent =
      "\uFEFF" +
      [
        headers.map(escapeVal).join(","),
        ...sampleRows.map((r) => r.map(escapeVal).join(",")),
      ].join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "department_import_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Drag & Drop Handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setImportFile(e.dataTransfer.files[0]);
      setImportResult(null);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImportFile(e.target.files[0]);
      setImportResult(null);
    }
  };

  // Submit Bulk Import
  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!importFile) return;

    const postData = new FormData();
    postData.append("file", importFile);

    try {
      setImportLoading(true);
      setImportResult(null);
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/auth/bulk-import-departments`,
        postData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setImportResult({
        success: true,
        message: res.data?.message || "Import completed successfully!",
        data: res.data,
      });
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchDepartments();
    } catch (err) {
      setImportResult({
        success: false,
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to import department accounts.",
      });
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className="admin-dept-split-grid">
      {/* Left Column: Department List */}
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "22px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          border: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <div style={{ fontSize: "15px", fontWeight: 600, color: "#0f172a" }}>
            Department Accounts ({departments.length})
          </div>
          <button
            type="button"
            onClick={fetchDepartments}
            style={{
              background: "none",
              border: "none",
              fontSize: "12px",
              color: "#ea580c",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Refresh List
          </button>
        </div>

        {/* Feedback Alert if any */}
        {feedback.text && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: "6px",
              marginBottom: "16px",
              backgroundColor: feedback.type === "success" ? "#ecfdf5" : "#fef2f2",
              border:
                feedback.type === "success"
                  ? "1px solid #a7f3d0"
                  : "1px solid #fecaca",
              color: feedback.type === "success" ? "#065f46" : "#991b1b",
              fontSize: "12.5px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{feedback.text}</span>
            <button
              onClick={() => setFeedback({ text: "", type: "" })}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "14px",
                color: "inherit",
              }}
            >
              ✕
            </button>
          </div>
        )}

        {loading ? (
          <div
            style={{
              padding: "36px 16px",
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "13px",
            }}
          >
            Loading departments...
          </div>
        ) : departments.length === 0 ? (
          <div
            style={{
              padding: "24px 16px",
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "13px",
              background: "#f8fafc",
              borderRadius: "6px",
              border: "1px dashed #cbd5e1",
            }}
          >
            No department accounts found.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {departments.map((dept) => {
              const isPrimaryAdmin =
                dept.role === "admin" ||
                dept.department?.toLowerCase() === "admin" ||
                dept.email?.toLowerCase().startsWith("admin@");

              return (
                <div
                  key={dept._id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 14px",
                    borderRadius: "8px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    transition: "all 0.15s ease",
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1, paddingRight: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "2px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "13.5px",
                          fontWeight: 600,
                          color: "#0f172a",
                        }}
                      >
                        {dept.department}
                      </span>
                      {isPrimaryAdmin && (
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            padding: "1px 7px",
                            borderRadius: "10px",
                            backgroundColor: "#fef3c7",
                            color: "#b45309",
                            border: "1px solid #fde68a",
                          }}
                        >
                          Primary Admin
                        </span>
                      )}
                    </div>

                    <div
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {dept.email}
                    </div>

                    {/* Associated Dues count */}
                    {dept.duesTotal !== undefined && (
                      <div
                        style={{
                          fontSize: "11.5px",
                          color: "#94a3b8",
                          marginTop: "4px",
                        }}
                      >
                        {dept.duesTotal} dues recorded •{" "}
                        <span
                          style={{
                            color:
                              dept.duesPending > 0 ? "#ea580c" : "#10b981",
                            fontWeight: 500,
                          }}
                        >
                          {dept.duesPending} pending
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {!isPrimaryAdmin && (
                    <button
                      type="button"
                      title="Remove Department Account"
                      onClick={() => handleDeleteDepartment(dept)}
                      disabled={actionLoading}
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "6px",
                        border: "1px solid #fee2e2",
                        backgroundColor: "#fff",
                        color: "#dc2626",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#fef2f2";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#fff";
                      }}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Right Column: Add / Bulk Import Department Form */}
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "22px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          border: "1px solid #e2e8f0",
        }}
      >
        {/* Top Header & Mode Toggle */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            borderBottom: "1px solid #f1f5f9",
            paddingBottom: "12px",
          }}
        >
          <div style={{ fontSize: "15px", fontWeight: 600, color: "#0f172a" }}>
            {activeMode === "single" ? "Add Department" : "Bulk Import Departments"}
          </div>

          {/* Mode Switcher */}
          <div
            style={{
              display: "flex",
              background: "#f1f5f9",
              padding: "3px",
              borderRadius: "8px",
              gap: "4px",
            }}
          >
            <button
              type="button"
              onClick={() => {
                setActiveMode("single");
                setImportResult(null);
              }}
              style={{
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: 500,
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
                backgroundColor: activeMode === "single" ? "#fff" : "transparent",
                color: activeMode === "single" ? "#ea580c" : "#64748b",
                boxShadow:
                  activeMode === "single" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                transition: "all 0.15s ease",
              }}
            >
              Single Entry
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveMode("import");
                setImportResult(null);
              }}
              style={{
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: 500,
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
                backgroundColor: activeMode === "import" ? "#fff" : "transparent",
                color: activeMode === "import" ? "#ea580c" : "#64748b",
                boxShadow:
                  activeMode === "import" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                transition: "all 0.15s ease",
              }}
            >
              Import Excel / CSV
            </button>
          </div>
        </div>

        {/* MODE 1: SINGLE ENTRY FORM */}
        {activeMode === "single" && (
          <form noValidate onSubmit={handleCreateSubmit}>
            {/* Department Name */}
            <div style={{ marginBottom: "12px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: formErrors.department ? "#dc2626" : "#475569",
                  marginBottom: "5px",
                }}
              >
                Department Name
              </label>
              <input
                type="text"
                placeholder="Department Name"
                value={formData.department}
                onChange={(e) => handleChange("department", e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: formErrors.department
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  backgroundColor: formErrors.department ? "#fff5f5" : "#f8fafc",
                  fontSize: "13px",
                  color: "#1e293b",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {formErrors.department && (
                <div
                  style={{ color: "#dc2626", fontSize: "11px", marginTop: "3px" }}
                >
                  {formErrors.department}
                </div>
              )}
            </div>

            {/* Official Email */}
            <div style={{ marginBottom: "12px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: formErrors.email ? "#dc2626" : "#475569",
                  marginBottom: "5px",
                }}
              >
                Official Email
              </label>
              <input
                type="email"
                placeholder="Enter official email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: formErrors.email
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  backgroundColor: formErrors.email ? "#fff5f5" : "#f8fafc",
                  fontSize: "13px",
                  color: "#1e293b",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {formErrors.email && (
                <div
                  style={{ color: "#dc2626", fontSize: "11px", marginTop: "3px" }}
                >
                  {formErrors.email}
                </div>
              )}
            </div>

            {/* Access Password */}
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: formErrors.password ? "#dc2626" : "#475569",
                  marginBottom: "5px",
                }}
              >
                Access Password
              </label>
              <input
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: formErrors.password
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  backgroundColor: formErrors.password ? "#fff5f5" : "#f8fafc",
                  fontSize: "13px",
                  color: "#1e293b",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {formErrors.password && (
                <div
                  style={{ color: "#dc2626", fontSize: "11px", marginTop: "3px" }}
                >
                  {formErrors.password}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={actionLoading}
              style={{
                width: "100%",
                height: "40px",
                backgroundColor: "#ea580c",
                color: "#fff",
                fontWeight: 600,
                fontSize: "13.5px",
                borderRadius: "6px",
                border: "none",
                cursor: actionLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.15s ease",
              }}
            >
              {actionLoading ? "Adding..." : "Add Department"}
            </button>
          </form>
        )}

        {/* MODE 2: BULK IMPORT EXCEL / CSV */}
        {activeMode === "import" && (
          <div>
            {/* Result Alert if any */}
            {importResult && (
              <div
                style={{
                  padding: "12px 14px",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  backgroundColor: importResult.success ? "#f0fdf4" : "#fef2f2",
                  border: importResult.success
                    ? "1px solid #86efac"
                    : "1px solid #fca5a5",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: importResult.success ? "#166534" : "#991b1b",
                    fontSize: "13px",
                    fontWeight: 500,
                    marginBottom: importResult.data ? "8px" : "0",
                  }}
                >
                  {importResult.success ? (
                    <FiCheckCircle size={16} color="#16a34a" />
                  ) : (
                    <FiAlertCircle size={16} color="#dc2626" />
                  )}
                  <span>{importResult.message}</span>
                </div>

                {importResult.data && (
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: "10px",
                        fontSize: "11.5px",
                        fontWeight: 600,
                        backgroundColor: "#dcfce7",
                        color: "#15803d",
                        border: "1px solid #bbf7d0",
                      }}
                    >
                      ✓ {importResult.data.inserted || 0} Added
                    </span>

                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: "10px",
                        fontSize: "11.5px",
                        fontWeight: 600,
                        backgroundColor: "#fef3c7",
                        color: "#b45309",
                        border: "1px solid #fde68a",
                      }}
                    >
                      ⚠ {importResult.data.duplicates || 0} Duplicates Skipped
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Drag and Drop Zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() =>
                fileInputRef.current && fileInputRef.current.click()
              }
              style={{
                border: `2px dashed ${dragActive ? "#ea580c" : "#cbd5e1"}`,
                borderRadius: "10px",
                padding: "30px 16px",
                textAlign: "center",
                backgroundColor: dragActive ? "#fff7ed" : "#f8fafc",
                cursor: "pointer",
                marginBottom: "14px",
                transition: "all 0.2s ease",
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx, .xls, .csv"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <FiUploadCloud
                size={32}
                color={dragActive ? "#ea580c" : "#94a3b8"}
                style={{ marginBottom: "8px" }}
              />
              <div
                style={{
                  fontSize: "13.5px",
                  fontWeight: 600,
                  color: "#1e293b",
                  marginBottom: "3px",
                }}
              >
                {importFile
                  ? importFile.name
                  : "Click to browse or drop file here"}
              </div>
              <div style={{ fontSize: "11.5px", color: "#64748b" }}>
                {importFile
                  ? `${(importFile.size / 1024).toFixed(1)} KB`
                  : "Supports .xlsx, .xls, and .csv files"}
              </div>
            </div>

            {/* Action Row */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "10px",
              }}
            >
              {/* Download Template Button */}
              <button
                type="button"
                onClick={handleDownloadTemplate}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "7px 12px",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#475569",
                  background: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "background 0.15s ease",
                }}
              >
                <FiDownload size={13} /> Sample Template
              </button>

              {/* Upload Button */}
              <button
                type="button"
                disabled={!importFile || importLoading}
                onClick={handleImportSubmit}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "7px 16px",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: "#fff",
                  backgroundColor:
                    !importFile || importLoading ? "#fdba74" : "#ea580c",
                  border: "none",
                  borderRadius: "6px",
                  cursor:
                    !importFile || importLoading ? "not-allowed" : "pointer",
                  transition: "background-color 0.15s ease",
                }}
              >
                <FiUploadCloud size={14} />
                {importLoading ? "Importing..." : "Upload & Import"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DepartmentManagement;
